import {
  Avatar,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Add, DeleteOutline } from "@mui/icons-material";
import { useState } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { SelectEmployee } from "components/SelectEmployee";
import ApiCalls from "services/flick_erp_api_call";
import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";
import { base_url } from "services";
import { useTheme } from "@mui/material/styles";
import { useMaterialUIController } from "context";

const normalizeImage = (src) => {
  if (!src) return "";
  return src.startsWith("http") ? src : `${base_url}${src}`;
};

export function ReportingManager({ data, permissions, modulePermissions, reloadCallback }) {
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const palette = theme.palette;
  const [selectedManagerId, setManagerId] = useState(null);
  const [selectedHrManagerId, setHrManagerId] = useState(null);
  const [addManagerDialog, setAddManagerDialog] = useState(false);
  const [addManagerError, setAddManagerError] = useState("");
  const [addHrManagerError, setHrAddManagerError] = useState("");

  const managerData = data?.manager
    ? { id: data.manager, name: data.manager_name, img: data.manager_profile, role: "Reporting Manager" }
    : null;
  const hrData = data?.hr_manager
    ? { id: data.hr_manager, name: data.hr_manager_name, img: data.hr_profile, role: "HR Manager" }
    : null;
  const ui = {
    card: darkMode
      ? `linear-gradient(135deg, ${rgba(palette.background.muted, 0.88)} 0%, ${rgba(palette.background.card, 0.98)} 100%)`
      : `linear-gradient(135deg, ${rgba(colors.background.elevated, 0.8)} 0%, ${rgba(colors.white.main, 0.98)} 100%)`,
    border: darkMode ? rgba(palette.white.main, 0.08) : rgba(colors.secondary.main, 0.08),
    title: darkMode ? palette.text.focus : colors.dark.main,
    body: darkMode ? palette.text.main : colors.text.main,
    soft: darkMode ? rgba(palette.background.muted, 0.4) : rgba(colors.background.muted, 0.2),
  };

  async function addManager(isHr) {
    if (isHr ? selectedHrManagerId != null : selectedManagerId != null) {
      isHr ? setHrAddManagerError("") : setAddManagerError("");
      const res = await ApiCalls.editEmployee(
        data.id,
        isHr ? { hr_manager: selectedHrManagerId } : { manager: selectedManagerId },
        false
      );
      if (res.success) {
        setAddManagerDialog(false);
        reloadCallback("");
      }
    } else {
      isHr ? setHrAddManagerError("Please select a HR manager") : setAddManagerError("Please select a manager");
    }
  }

  async function deleteManager(isHr) {
    const res = await ApiCalls.editEmployee(
      data.id,
      isHr ? { hr_manager: null } : { manager: null },
      false
    );
    if (res.success) reloadCallback("");
  }

  const managerCard = (manager, isHr) => (
    <MDBox
      key={manager.id}
      sx={{
        p: 2,
        borderRadius: "22px",
        background: ui.card,
        border: `1px solid ${ui.border}`,
        boxShadow: darkMode ? "0 10px 24px rgba(2, 6, 23, 0.22)" : `0 10px 24px ${rgba(colors.dark.main, 0.05)}`,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
        <Stack direction="row" spacing={1.25} alignItems="center" minWidth={0}>
          <Avatar
            src={normalizeImage(manager.img)}
            sx={{
              width: 52,
              height: 52,
              bgcolor: colors.background.accent,
              color: colors.info.focus,
            }}
          >
            {manager.name?.charAt(0)}
          </Avatar>
          <MDBox minWidth={0}>
            <MDTypography variant="button" sx={{ color: ui.title, fontWeight: 700 }}>
              {manager.name}
            </MDTypography>
            <MDTypography variant="caption" sx={{ display: "block", mt: 0.35, color: ui.body }}>
              {manager.role}
            </MDTypography>
          </MDBox>
        </Stack>
        {permissions?.includes(modulePermissions?.update_permission) && (
          <IconButton
            onClick={() => deleteManager(isHr)}
            sx={{
              width: 36,
              height: 36,
              borderRadius: "12px",
              backgroundColor: rgba(colors.error.main, 0.1),
              color: colors.error.main,
            }}
          >
            <DeleteOutline sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Stack>
    </MDBox>
  );

  return (
    <>
      <MDBox p={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
          <MDBox>
            <MDTypography variant="h5" sx={{ color: ui.title }}>
              Management Team
            </MDTypography>
            <MDTypography variant="body2" sx={{ mt: 0.35, color: ui.body }}>
              Direct reporting and people support contacts.
            </MDTypography>
          </MDBox>
          {permissions.includes(modulePermissions.reporting_structure_create_permission) &&
            (data?.manager == null || data?.hr_manager == null) && (
              <IconButton
                onClick={() => setAddManagerDialog(true)}
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "14px",
                  backgroundColor: rgba(colors.info.main, 0.12),
                  color: colors.info.focus,
                }}
              >
                <Add />
              </IconButton>
            )}
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {hrData ? (
              managerCard(hrData, true)
            ) : (
              <MDBox
                sx={{
                  p: 2,
                  borderRadius: "22px",
                  border: `1px dashed ${ui.border}`,
                  backgroundColor: ui.soft,
                }}
              >
                <MDTypography variant="button" sx={{ color: ui.title }}>
                  HR Manager not assigned
                </MDTypography>
              </MDBox>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {managerData ? (
              managerCard(managerData, false)
            ) : (
              <MDBox
                sx={{
                  p: 2,
                  borderRadius: "22px",
                  border: `1px dashed ${ui.border}`,
                  backgroundColor: ui.soft,
                }}
              >
                <MDTypography variant="button" sx={{ color: ui.title }}>
                  Reporting Manager not assigned
                </MDTypography>
              </MDBox>
            )}
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={addManagerDialog}
        onClose={() => {
          setAddManagerDialog(false);
          setAddManagerError("");
          setHrAddManagerError("");
        }}
        PaperProps={{
          sx: {
            width: "min(560px, 92vw)",
            borderRadius: "24px",
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ color: ui.title, fontWeight: 700 }}>Add Manager</DialogTitle>
        <DialogContent>
          {data?.manager == null && (
            <MDBox mb={3}>
              <Typography sx={{ mb: 1 }}>Manager</Typography>
              <Grid container spacing={1.5} alignItems="flex-end">
                <Grid item xs={12} sm>
                  <SelectEmployee
                    label="Select Manager"
                    onSelected={(e) => {
                      setManagerId(e);
                      setAddManagerError("");
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm="auto">
                  <MDButton variant="gradient" color="info" onClick={() => addManager(false)}>
                    Add
                  </MDButton>
                </Grid>
              </Grid>
              {addManagerError && <MDBox mt={1} color="error.main">{addManagerError}</MDBox>}
            </MDBox>
          )}

          {data?.hr_manager == null && (
            <MDBox>
              <Typography sx={{ mb: 1 }}>HR Manager</Typography>
              <Grid container spacing={1.5} alignItems="flex-end">
                <Grid item xs={12} sm>
                  <SelectEmployee
                    label="Select HR Manager"
                    onSelected={(e) => {
                      setHrManagerId(e);
                      setHrAddManagerError("");
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm="auto">
                  <MDButton variant="gradient" color="info" onClick={() => addManager(true)}>
                    Add
                  </MDButton>
                </Grid>
              </Grid>
              {addHrManagerError && <MDBox mt={1} color="error.main">{addHrManagerError}</MDBox>}
            </MDBox>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
